/** Everything the public job page says to a candidate, per language.
 *
 * The ad itself is written per language by the recruiter; these are the words
 * around it — the form, the timeline, the failures. A language the ad is
 * published in but this file has no entry for falls back to English, which is
 * visible to the recruiter as an English form under a Swedish ad rather than
 * as a crash.
 *
 * The app's own UI is English only; this file is candidate-facing text.
 */

export interface PublicJobStrings {
	openPositions: string;
	noPositions: string;
	morePositions: string;
	listFailed: string;
	adFailed: string;
	adUnavailable: string;
	applicationNotFound: string;
	applyHeading: string;
	applyPromise: string;
	stepApply: string;
	stepReadsAnswers: string;
	stepReadsLetter: string;
	stepAlwaysAnswer: string;
	cvDropTitle: string;
	cvDropHint: string;
	name: string;
	email: string;
	phone: string;
	linkedin: string;
	questionsIntro: string;
	questionPlaceholder: string;
	coverLetter: string;
	letterDropTitle: string;
	letterDropHint: string;
	letterPlaceholder: string;
	letterInserted: string;
	letterUnreadable: string;
	consent: string;
	holdToSubmit: string;
	keepHolding: string;
	releaseToSubmit: string;
	heldTooLong: string;
	needCvOrLinkedin: string;
	submitFailed: string;
	submitted: string;
	submittedFollow: string;
	readingFile: string;
	parsedTitle: string;
	parsedFailed: string;
	yourApplication: string;
	appliedOn: string;
	statusReceived: string;
	statusReceivedHint: string;
	statusUnderReview: string;
	statusUnderReviewHint: string;
	statusAnswered: string;
	statusAnsweredHint: string;
	takenFurther: string;
	languageLabel: string;
}

const EN: PublicJobStrings = {
	openPositions: "Open positions",
	noPositions: "No open positions right now — check back soon.",
	morePositions: "More open positions",
	listFailed: "Could not load open positions.",
	adFailed: "Could not load this position.",
	adUnavailable: "This position is not available.",
	applicationNotFound: "We could not find this application.",
	applyHeading: "Apply for this position",
	applyPromise:
		"Takes under a minute — we read everything from your CV, even your name.",
	stepApply: "<strong>Apply</strong> — CV or LinkedIn profile is enough",
	stepReadsAnswers:
		"<strong>A recruiter reads your answers first</strong> — always, before anything else",
	stepReadsLetter:
		"<strong>A recruiter reads your cover letter first</strong> — always, before anything else",
	stepAlwaysAnswer:
		"<strong>You always get an answer</strong> — whichever way it goes",
	cvDropTitle: "Start with your CV — drop it here or click to browse",
	cvDropHint:
		"PDF, Word or text. We read it and fill in the form for you. No CV? Add your LinkedIn profile below instead.",
	name: "Name",
	email: "Email",
	phone: "Phone",
	linkedin: "LinkedIn profile URL",
	questionsIntro:
		"A few questions from the recruiter — answer them instead of writing a cover letter.",
	questionPlaceholder: "A few sentences is enough…",
	coverLetter: "Cover letter",
	letterDropTitle: "Drop your cover letter here, or write it below",
	letterDropHint: "PDF, Word or text — the text is inserted below for editing.",
	letterPlaceholder: "Why this role fits you…",
	letterInserted: "text inserted below (editable)",
	letterUnreadable: "Could not read that file — please paste the text below.",
	consent:
		"I consent to the processing of my personal data for this recruitment process.",
	holdToSubmit: "Hold to submit",
	keepHolding: "Keep holding…",
	releaseToSubmit: "Release to submit!",
	heldTooLong: "Held too long — try again",
	needCvOrLinkedin: "Attach a CV or supply your LinkedIn profile URL.",
	submitFailed: "Could not submit the application.",
	submitted:
		"Thank you — your application has been received. A confirmation is on its way to your email.",
	submittedFollow: "Follow its status here (bookmark this link):",
	readingFile: "Reading",
	parsedTitle:
		"This is what we read from your CV — please check that it looks right:",
	parsedFailed:
		"We could not read that file — fill in the form below, or add your LinkedIn profile.",
	yourApplication: "Your application",
	appliedOn: "applied",
	statusReceived: "Received",
	statusReceivedHint: "Your application is in.",
	statusUnderReview: "Under review",
	statusUnderReviewHint: "A recruiter is looking at your application.",
	statusAnswered: "Answered",
	statusAnsweredHint: "We have sent you our decision by email.",
	takenFurther:
		"Good news — a recruiter is taking your application further. Expect to hear from us about the next step.",
	languageLabel: "Language",
};

const SV: PublicJobStrings = {
	openPositions: "Lediga tjänster",
	noPositions: "Inga lediga tjänster just nu — titta gärna in igen.",
	morePositions: "Fler lediga tjänster",
	listFailed: "Kunde inte hämta de lediga tjänsterna.",
	adFailed: "Kunde inte hämta den här tjänsten.",
	adUnavailable: "Den här tjänsten är inte tillgänglig.",
	applicationNotFound: "Vi hittar inte den här ansökan.",
	applyHeading: "Sök tjänsten",
	applyPromise:
		"Tar under en minut — vi läser allt från ditt CV, till och med ditt namn.",
	stepApply: "<strong>Ansök</strong> — CV eller LinkedIn-profil räcker",
	stepReadsAnswers:
		"<strong>En rekryterare läser dina svar först</strong> — alltid, före allt annat",
	stepReadsLetter:
		"<strong>En rekryterare läser ditt personliga brev först</strong> — alltid, före allt annat",
	stepAlwaysAnswer: "<strong>Du får alltid svar</strong> — hur det än går",
	cvDropTitle: "Börja med ditt CV — släpp det här eller klicka för att välja",
	cvDropHint:
		"PDF, Word eller text. Vi läser det och fyller i formuläret åt dig. Inget CV? Lägg in din LinkedIn-profil nedan i stället.",
	name: "Namn",
	email: "E-post",
	phone: "Telefon",
	linkedin: "Länk till LinkedIn-profil",
	questionsIntro:
		"Några frågor från rekryteraren — svara på dem i stället för att skriva ett personligt brev.",
	questionPlaceholder: "Några meningar räcker…",
	coverLetter: "Personligt brev",
	letterDropTitle: "Släpp ditt personliga brev här, eller skriv det nedan",
	letterDropHint: "PDF, Word eller text — texten läggs in nedan för redigering.",
	letterPlaceholder: "Varför den här rollen passar dig…",
	letterInserted: "texten är inlagd nedan (går att redigera)",
	letterUnreadable: "Kunde inte läsa filen — klistra in texten nedan.",
	consent:
		"Jag samtycker till att mina personuppgifter behandlas i den här rekryteringen.",
	holdToSubmit: "Håll in för att skicka",
	keepHolding: "Fortsätt hålla in…",
	releaseToSubmit: "Släpp för att skicka!",
	heldTooLong: "Du höll in för länge — försök igen",
	needCvOrLinkedin: "Bifoga ett CV eller lägg in länken till din LinkedIn-profil.",
	submitFailed: "Kunde inte skicka ansökan.",
	submitted:
		"Tack — vi har fått din ansökan. En bekräftelse är på väg till din e-post.",
	submittedFollow: "Följ din ansökan här (spara länken):",
	readingFile: "Läser",
	parsedTitle: "Det här läste vi i ditt CV — stämmer det?",
	parsedFailed:
		"Vi kunde inte läsa filen — fyll i formuläret nedan, eller lägg in din LinkedIn-profil.",
	yourApplication: "Din ansökan",
	appliedOn: "ansökte",
	statusReceived: "Mottagen",
	statusReceivedHint: "Din ansökan är inne.",
	statusUnderReview: "Under granskning",
	statusUnderReviewHint: "En rekryterare tittar på din ansökan.",
	statusAnswered: "Besvarad",
	statusAnsweredHint: "Vi har skickat vårt besked till din e-post.",
	takenFurther:
		"Goda nyheter — en rekryterare går vidare med din ansökan. Du hör från oss om nästa steg.",
	languageLabel: "Språk",
};

const DE: PublicJobStrings = {
	openPositions: "Offene Stellen",
	noPositions: "Zurzeit keine offenen Stellen — schauen Sie bald wieder vorbei.",
	morePositions: "Weitere offene Stellen",
	listFailed: "Die offenen Stellen konnten nicht geladen werden.",
	adFailed: "Diese Stelle konnte nicht geladen werden.",
	adUnavailable: "Diese Stelle ist nicht verfügbar.",
	applicationNotFound: "Diese Bewerbung finden wir nicht.",
	applyHeading: "Auf diese Stelle bewerben",
	applyPromise:
		"Dauert unter einer Minute — wir lesen alles aus Ihrem Lebenslauf, auch Ihren Namen.",
	stepApply: "<strong>Bewerben</strong> — Lebenslauf oder LinkedIn-Profil genügt",
	stepReadsAnswers:
		"<strong>Eine Recruiterin liest zuerst Ihre Antworten</strong> — immer, vor allem anderen",
	stepReadsLetter:
		"<strong>Eine Recruiterin liest zuerst Ihr Anschreiben</strong> — immer, vor allem anderen",
	stepAlwaysAnswer: "<strong>Sie bekommen immer eine Antwort</strong> — so oder so",
	cvDropTitle:
		"Beginnen Sie mit Ihrem Lebenslauf — hier ablegen oder zum Auswählen klicken",
	cvDropHint:
		"PDF, Word oder Text. Wir lesen ihn und füllen das Formular für Sie aus. Kein Lebenslauf? Tragen Sie unten Ihr LinkedIn-Profil ein.",
	name: "Name",
	email: "E-Mail",
	phone: "Telefon",
	linkedin: "Link zum LinkedIn-Profil",
	questionsIntro:
		"Ein paar Fragen der Recruiterin — beantworten Sie sie statt ein Anschreiben zu verfassen.",
	questionPlaceholder: "Ein paar Sätze genügen…",
	coverLetter: "Anschreiben",
	letterDropTitle: "Anschreiben hier ablegen oder unten schreiben",
	letterDropHint:
		"PDF, Word oder Text — der Text wird unten zum Bearbeiten eingefügt.",
	letterPlaceholder: "Warum diese Rolle zu Ihnen passt…",
	letterInserted: "Text unten eingefügt (bearbeitbar)",
	letterUnreadable:
		"Die Datei konnte nicht gelesen werden — fügen Sie den Text unten ein.",
	consent:
		"Ich willige in die Verarbeitung meiner personenbezogenen Daten für dieses Bewerbungsverfahren ein.",
	holdToSubmit: "Zum Senden gedrückt halten",
	keepHolding: "Weiter halten…",
	releaseToSubmit: "Loslassen zum Senden!",
	heldTooLong: "Zu lange gehalten — bitte erneut versuchen",
	needCvOrLinkedin:
		"Hängen Sie einen Lebenslauf an oder geben Sie Ihr LinkedIn-Profil an.",
	submitFailed: "Die Bewerbung konnte nicht gesendet werden.",
	submitted:
		"Danke — wir haben Ihre Bewerbung erhalten. Eine Bestätigung ist per E-Mail unterwegs.",
	submittedFollow: "Hier verfolgen Sie den Stand (Link merken):",
	readingFile: "Lese",
	parsedTitle: "Das haben wir aus Ihrem Lebenslauf gelesen — stimmt das?",
	parsedFailed:
		"Wir konnten die Datei nicht lesen — füllen Sie das Formular unten aus oder geben Sie Ihr LinkedIn-Profil an.",
	yourApplication: "Ihre Bewerbung",
	appliedOn: "beworben am",
	statusReceived: "Eingegangen",
	statusReceivedHint: "Ihre Bewerbung ist da.",
	statusUnderReview: "In Prüfung",
	statusUnderReviewHint: "Eine Recruiterin sieht sich Ihre Bewerbung an.",
	statusAnswered: "Beantwortet",
	statusAnsweredHint: "Wir haben Ihnen unsere Entscheidung per E-Mail geschickt.",
	takenFurther:
		"Gute Nachrichten — eine Recruiterin geht mit Ihrer Bewerbung weiter. Sie hören von uns zum nächsten Schritt.",
	languageLabel: "Sprache",
};

const STRINGS: Record<string, PublicJobStrings> = { en: EN, sv: SV, de: DE };

/** The language names as their own readers write them, for the switch. */
const LANGUAGE_NAMES: Record<string, string> = {
	en: "English",
	sv: "Svenska",
	de: "Deutsch",
	da: "Dansk",
	nb: "Norsk",
	no: "Norsk",
	fi: "Suomi",
	nl: "Nederlands",
	fr: "Français",
	es: "Español",
	it: "Italiano",
	pl: "Polski",
	pt: "Português",
};

export function strings(language: string): PublicJobStrings {
	return STRINGS[language] ?? EN;
}

export function languageName(language: string): string {
	return LANGUAGE_NAMES[language] ?? language.toUpperCase();
}

/** The reader's own language when the ad has it, else the ad's first.
 *
 * `navigator.languages` is in the reader's order of preference, so the first
 * match wins rather than the ad's order.
 */
export function preferredLanguage(available: string[], asked: string | null): string {
	if (asked && available.includes(asked)) return asked;
	for (const tag of navigator.languages ?? []) {
		const base = tag.split("-")[0].toLowerCase();
		if (available.includes(base)) return base;
	}
	return available[0] ?? "en";
}
